import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageContainer from '../../../layouts/PageContainer';
import PageHeader from '../../../components/Nav/PageHeader';
import { useConfig } from '../../../DataStore/ConfigContext';
import ActivityLoader from '../../../components/Common/ActivityLoader';
import { generateLocalConfigText } from '../../../util/router_scripts';

const ROUTER_TYPES = [
  { value: 'mikrotik', label: 'MikroTik' },
  { value: 'cisco', label: 'Cisco' },
  { value: 'ubiquiti', label: 'Ubiquiti' },
  { value: 'tp-link', label: 'TP-Link' },
  { value: 'other', label: 'Other' },
];

const BASE_URL = 'https://openfi-login.araknerd.com'; // central controller (adjust)

// Map API shape -> UI shape
const mapApiRouterToUi = (r) => ({
  id: String(r.id),
  name: r.name || '',
  ssid: r.SSID || 'Openfi Network',
  site: r.site || null,
  type: String(r.router_type || 'other').toLowerCase(),
  model: r.model || null,
  serial: r.serial || null,
  mac: r.mac || null,
  ip: r.ip_address || null,
  wanIp: r.wan_ip || null,
  sshPort: r.ssh_port != null ? Number(r.ssh_port) : 22,
  apiPort: r.api_port != null ? Number(r.api_port) : null,
  hotspotName: r.hotspot_name || null,
  radiusEnabled: String(r.radius_enabled ?? '1') === '1',
  portalVisible: String(r.is_visible ?? '1') === '1',
  status: r.status || 'inactive',
  active: (r.status || 'inactive') === 'active',
  location: r.location || null,
  latitude: r.latitude || null,
  longitude: r.longitude || null,
  notes: r.notes || null,
  lastHeartbeat: r.last_heartbeat ? Date.parse(r.last_heartbeat) : null,
  token: r.token || null, // present if your backend includes it
});

// Label helper
const typeToLabel = (t) => (ROUTER_TYPES.find((x) => x.value === (t || 'other'))?.label || 'Other');

export default function RoutersPage() {
  const navigate = useNavigate();
  const { getRouters, selectRouter, routers, selectedRouter } = useConfig();

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null); // { type:'success'|'error', msg:string }
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState({ status: 'all', site: 'all', type: 'all' });
  const [editActive, setEditActive] = useState(null);

  // Local presentation state derived from context routers
  const [rows, setRows] = useState([]);

  useEffect(()=>{
    if (selectedRouter && editActive) {
      navigate('/settings/routers-edit');
    }
  }, [selectedRouter, editActive, navigate]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await getRouters(); // populates `routers` in context
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When context routers change, map to UI rows
  useEffect(() => {
    const mapped = Array.isArray(routers) ? routers.map(mapApiRouterToUi) : [];
    setRows(mapped);
  }, [routers]);

  const handleNew = () => navigate('/settings/routers-new');

  const handleEdit = (r) => {
    setEditActive(true);
    selectRouter(r);
  };

  const handleToggleActive = (id) => {
    setRows((list) =>
      list.map((r) =>
        String(r.id) === String(id)
          ? { ...r, active: !r.active, status: r.active ? 'inactive' : 'active' }
          : r
      )
    );
    // TODO: call backend to persist status toggle
  };

  const handleDelete = (id) => {
    if (!window.confirm('Archive/remove this router? This will not delete past session history.')) return;
    setRows((list) => list.filter((r) => String(r.id) !== String(id)));
    // TODO: call backend to archive/delete
  };

  const handleHeartbeatTest = () =>
    setStatus({ type: 'success', msg: 'Ping sent. If online, heartbeat will update shortly.' });

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setStatus({ type: 'success', msg: 'Copied to clipboard.' });
    } catch {
      setStatus({ type: 'error', msg: 'Copy failed.' });
    }
  };

  const buildConfigFilename = (r) => {
    const slug = `${(r.name || 'router').replace(/\s+/g, '_')}_${r.id}`;
    const ext =
      r.type === 'mikrotik' ? 'rsc'
      : r.type === 'cisco' ? 'txt'
      : r.type === 'ubiquiti' ? 'cfg'
      : 'txt';
    return `openfi_${slug}.${ext}`;
  };

 

  const downloadTextFile = (text, filename) => {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadConfig = async (r) => {
    try {
      // TODO: replace with real API call to fetch vendor config
      const text = generateLocalConfigText(r, BASE_URL);
      downloadTextFile(text, buildConfigFilename(r));
      setStatus({ type: 'success', msg: 'Config downloaded.' });
    } catch {
      setStatus({ type: 'error', msg: 'Failed to download config.' });
    }
  };

  const isOnline = (r) => r.lastHeartbeat && (Date.now() - r.lastHeartbeat) < 10 * 60 * 1000;

  // Filters/search over UI rows
  const filtered = useMemo(() => {
    let list = [...rows];
    if (filter.status !== 'all') list = list.filter((r) => (filter.status === 'active' ? r.active : !r.active));
    if (filter.site !== 'all') list = list.filter((r) => (r.site || '').toLowerCase() === filter.site.toLowerCase());
    if (filter.type !== 'all') list = list.filter((r) => (r.type || 'other') === filter.type);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((r) =>
        [r.name, r.site, r.model, r.serial, r.mac, r.wanIp, r.ip, r.location]
          .some((x) => (x || '').toLowerCase().includes(q))
      );
    }
    return list.sort(
      (a, b) =>
        (a.site || '').localeCompare(b.site || '') ||
        (a.name || '').localeCompare(b.name || '')
    );
  }, [rows, filter, query]);

  const uniqueSites = useMemo(() => {
    const s = new Set(rows.map((r) => r.site).filter(Boolean));
    return ['all', ...Array.from(s)];
  }, [rows]);

  const buildProvisionScript = (r) => {
    if ((r.type || 'other') !== 'mikrotik' || !r.token) return null;
    return `/tool fetch url="${BASE_URL}/bootstrap.rsc?router_id=${r.id}&token=${encodeURIComponent(
      r.token
    )}" dst-path=bootstrap.rsc mode=https ; /import file-name=bootstrap.rsc`;
  };

  return (
    <PageContainer>
      <PageHeader
        title="Routers"
        breadcrumb={
          <>
            <li className="breadcrumb-item"><Link to="/settings">Settings</Link></li>
            <li className="breadcrumb-item active" aria-current="page">Routers</li>
          </>
        }
      />

        <div className="row mb-3">
          <div className="col-12">
            <div className="alert alert-info mb-0">
              Manage routers by vendor type. Use <strong>New Router</strong> to register, then download the vendor config or use the MikroTik one-liner to bootstrap.
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div className="row align-items-center mb-3">
          <div className="col-md-6 d-flex">
            <button className="btn btn-primary mr-2" onClick={handleNew}>
              <i className="fa fa-plus mr-1" /> New Router
            </button>
          </div>
          <div className="col-md-6 d-flex justify-content-md-end mt-2 mt-md-0">
            <input
              className="form-control w-50 mr-2"
              placeholder="Search name, MAC, IP, site…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <select
              className="form-control w-auto mr-2"
              value={filter.type}
              onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value }))}
            >
              <option value="all">All types</option>
              {ROUTER_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <select
              className="form-control w-auto mr-2"
              value={filter.site}
              onChange={(e) => setFilter((f) => ({ ...f, site: e.target.value }))}
            >
              {uniqueSites.map((s) => (
                <option key={s} value={s}>{s === 'all' ? 'All sites' : s}</option>
              ))}
            </select>
            <select
              className="form-control w-auto"
              value={filter.status}
              onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Global status */}
        {status?.msg && (
          <div className={`alert ${status.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
            {status.msg}
          </div>
        )}

        {/* Routers table */}
        <div className="row mt-3">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Registered Routers</h6>
                  {loading && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
                </div>

                <div className="table-responsive">
                  <table className="table table-hover table-sm mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: 40 }}>#</th>
                        <th>Name / Site</th>
                        <th>Type</th>
                        <th>Model / Serial</th>
                        <th>MAC / WAN IP</th>
                        <th>Hotspot</th>
                        <th>Health</th>
                        <th style={{ width: 380 }}>Actions & Provisioning</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 && !loading && (
                        <tr>
                          <td colSpan="8" className="text-center text-muted py-4">No routers found.</td>
                        </tr>
                      )}
                      {filtered.map((r, idx) => {
                        const online = isOnline(r);
                        const script = buildProvisionScript(r);
                        const typeLabel = typeToLabel(r.type);
                        return (
                          <tr key={r.id}>
                            <td>{idx + 1}</td>
                            <td>
                              <div className="font-weight-600">{r.name}</div>
                              <div className="small text-muted">{r.site}{r.location ? ` • ${r.location}` : ''}</div>
                            </td>
                            <td className="text-capitalize">
                              {typeLabel}
                              <div className="small text-muted">{r.ssid || '—'}</div>
                            </td>
                            <td>
                              <div>{r.model || '—'}</div>
                              <div className="small text-muted">{r.serial || '—'}</div>
                            </td>
                            <td>
                              <div>{r.mac || '—'}</div>
                              <div className="small text-muted">{r.wanIp || r.ip || '—'}</div>
                            </td>
                            <td>
                              {r.type === 'mikrotik' ? (
                                <>
                                  <div>{r.hotspotName || '—'}</div>
                                  <div className="small text-muted">API:{r.apiPort ?? '—'} • SSH:{r.sshPort}</div>
                                </>
                              ) : (
                                <div className="small text-muted">SSH:{r.sshPort}</div>
                              )}
                            </td>
                            <td>
                              {online ? (
                                <span className="badge badge-success">Online</span>
                              ) : (
                                <span className="badge badge-secondary">Offline</span>
                              )}
                              <div className="small text-muted">
                                {r.lastHeartbeat
                                  ? `${Math.round((Date.now() - r.lastHeartbeat) / 60000)} min ago`
                                  : '—'}
                              </div>
                            </td>
                            <td>
                              <div className="btn-group btn-group-sm mb-1">
                                <button className="btn btn-outline-primary" onClick={() => handleEdit(r)}>
                                  <i className="fa fa-edit mr-1" /> Edit
                                </button>
                                <button
                                  className={`btn ${r.active ? 'btn-success' : 'btn-outline-secondary'}`}
                                  onClick={() => handleToggleActive(r.id)}
                                  title={r.active ? 'Deactivate' : 'Activate'}
                                >
                                  {r.active ? 'Active' : 'Inactive'}
                                </button>
                                {/* <button className="btn btn-outline-danger" onClick={() => handleDelete(r.id)}>
                                  <i className="fa fa-archive mr-1" /> Archive
                                </button> */}
                              </div>

                              {/* Provisioning controls */}
                              {script ? (
                                <>
                                  <div className="input-group input-group-sm mb-1">
                                    <input
                                      className="form-control"
                                      readOnly
                                      value={script}
                                      onFocus={(e) => e.target.select()}
                                      title="MikroTik provisioning one-liner"
                                    />
                                    <div className="input-group-append">
                                      <button className="btn btn-outline-secondary" onClick={() => copy(script)} title="Copy">
                                        <i className="far fa-copy" />
                                      </button>
                                      <button className="btn btn-outline-secondary" onClick={handleHeartbeatTest} title="Ping">
                                        <i className="fa fa-plug" />
                                      </button>
                                    </div>
                                  </div>
                                  <button className="btn btn-outline-primary btn-sm" onClick={() => handleDownloadConfig(r)} title="Download .rsc config">
                                    <i className="fa fa-download mr-1" />
                                    Download Config (.rsc)
                                  </button>
                                </>
                              ) : (
                                <button className="btn btn-outline-primary btn-sm" onClick={() => handleDownloadConfig(r)} title="Download vendor config">
                                  <i className="fa fa-download mr-1" />
                                  Download Config
                                </button>
                              )}

                              {r.token && (
                                <div className="small text-muted mt-1">
                                  Token: <code>{r.token}</code>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {loading && <ActivityLoader />}
                </div>

                <div className="small text-muted mt-2">
                  Archiving hides the router but keeps historical sessions and revenue data intact.
                </div>
              </div>
            </div>
          </div>
        </div>

    </PageContainer>
  );
}
